import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Boxes,
  CheckCircle2,
  MinusCircle,
  Waypoints,
  XCircle,
} from 'lucide-react';
import type { DeployStatus, GenieacsStatus, TunnelStatus } from '@simpleacs/shared';
import { api } from '../../lib/api';
import { formatBytes, formatRelative, truncate } from '../../lib/format';
import {
  Card,
  EmptyState,
  Pill,
  Spinner,
  TBody,
  THead,
  TRow,
  Table,
} from '../../components/ui';

/**
 * What the platform runs on, for the superadmin who maintains it.
 *
 * These were a separate Infrastructure page whose index was a list of five
 * links, three of which restated what the Status page already showed —
 * endpoints, tunnel addresses, whether GenieACS answers. Only the checks that
 * cannot be seen anywhere else survived the move: whether GenieACS still holds
 * its stock provisions, what the tunnels actually carry, and whether this
 * install passes its own runbook. They sit under Status now because that is
 * where somebody looks when they suspect the platform rather than a subscriber.
 */

/** ok=true → green check, ok=false → red x, ok=null → grey "check by hand". */
export function CheckIcon({ ok }: { ok: boolean | null }) {
  if (ok === true) return <CheckCircle2 className="size-4 text-success" />;
  if (ok === false) return <XCircle className="size-4 text-danger" />;
  return <MinusCircle className="size-4 text-faint" />;
}

export function ObjectList({ label, names }: { label: string; names: string[] }) {
  return (
    <div>
      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-faint">{label}</p>
      {names.length === 0 ? (
        <p className="text-sm text-faint">none</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {names.map((n) => (
            <span
              key={n}
              className="rounded-full border border-line px-2 py-0.5 font-mono text-xs text-muted"
            >
              {n}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The management-plane tunnels, past the addresses the Configuration card
 * already lists: which peers exist and when each last spoke. A tunnel that is
 * configured and never handshakes looks identical to a working one everywhere
 * else in the platform.
 */
export function TunnelsCard() {
  const query = useQuery({
    queryKey: ['infra', 'tunnels'],
    queryFn: () => api.get<TunnelStatus>('/infra/tunnels'),
    refetchInterval: 60_000,
  });
  const t = query.data;

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Waypoints className="size-4 text-muted" />
          Tunnels
        </span>
      }
      action={
        <Link to="/gateways" className="text-xs text-link hover:underline">
          Manage gateways
        </Link>
      }
    >
      {query.isLoading || !t ? (
        <div className="flex justify-center p-8">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-line px-5 py-3 text-xs">
            <span className="text-muted">
              WireGuard key{' '}
              <span className="font-mono text-ink">
                {t.wg.serverPublicKey ? truncate(t.wg.serverPublicKey, 20) : 'not set'}
              </span>
            </span>
            <span className="text-muted">
              OpenVPN PKI{' '}
              <span className="text-ink">
                {t.ovpn.pkiPresent === null ? 'unknown' : t.ovpn.pkiPresent ? 'present' : 'missing'}
              </span>
            </span>
            <span className="text-muted">
              OpenVPN clients{' '}
              <span className="tabular-nums text-ink">
                {t.ovpn.connectedClients === null ? '—' : t.ovpn.connectedClients}
              </span>
            </span>
          </div>
          {t.wg.peers.length === 0 ? (
            <EmptyState
              title="No WireGuard peers"
              description={
                t.execAdapter === 'dry-run'
                  ? 'This deployment runs the dry-run adapter.'
                  : 'The interface may be down, or no gateway has been created yet.'
              }
            />
          ) : (
            <Table>
              <THead>
                <th className="th">Peer</th>
                <th className="th">Handshake</th>
                <th className="th">Rx / Tx</th>
              </THead>
              <TBody>
                {t.wg.peers.map((p) => (
                  <TRow key={p.publicKey}>
                    <td className="td font-mono text-xs">{truncate(p.publicKey, 16)}</td>
                    <td className="td text-muted">
                      {p.latestHandshake ? (
                        formatRelative(p.latestHandshake * 1000)
                      ) : (
                        <span className="text-faint">never</span>
                      )}
                    </td>
                    <td className="td tabular-nums text-muted">
                      {formatBytes(p.rxBytes)} / {formatBytes(p.txBytes)}
                    </td>
                  </TRow>
                ))}
              </TBody>
            </Table>
          )}
        </>
      )}
    </Card>
  );
}

/** This install: version, migrations and the runbook checks it can answer itself. */
export function DeploymentCard() {
  const query = useQuery({
    queryKey: ['infra', 'deploy'],
    queryFn: () => api.get<DeployStatus>('/infra/deploy'),
    refetchInterval: 60_000,
  });
  const d = query.data;
  const failing = d?.checks.filter((c) => c.ok === false).length ?? 0;

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Boxes className="size-4 text-muted" />
          Deployment
          {d && (
            <span className="text-xs font-normal text-faint">
              v{d.version} · {d.migrations.applied} migrations
            </span>
          )}
          {failing > 0 && (
            <Pill tone="danger" dot>
              {failing} failing
            </Pill>
          )}
        </span>
      }
    >
      {query.isLoading || !d ? (
        <div className="flex justify-center p-8">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="divide-y divide-line">
            {d.checks.map((c) => (
              <div key={c.id} className="flex items-start gap-3 px-5 py-3">
                <span className="pt-0.5">
                  <CheckIcon ok={c.ok} />
                </span>
                <div className="min-w-0">
                  <p className="text-sm text-ink">{c.label}</p>
                  <p className="text-[11px] text-faint">{c.detail}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="border-t border-line px-5 py-2.5 text-[11px] text-faint">
            Grey checks can only be confirmed on the host — they stay neutral until verified there.
          </p>
        </>
      )}
    </Card>
  );
}
